import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { SHIFT } from '@kioskgaming/ui';
import CashierLayout from '../components/CashierLayout';
import DenominationGrid from '../components/DenominationGrid';
import { cashierGetShift, cashierStartShift } from '../services/cashierApi';
import { emptyDenominationGrid } from '../utils/denominations';
import { describeDeviceError, isStartBlockerCode } from '../utils/deviceErrors';
import { isShiftViewable, shiftNotViewableMessage } from '../utils/shiftAccess';

export default function OpeningCashPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [denominations, setDenominations] = useState(emptyDenominationGrid());

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await cashierGetShift(id);
        if (!res.success || !res.data?.shift) {
          toast.error(res.message || 'Shift not found');
          navigate('/', { replace: true });
          return;
        }
        const shiftStatus = res.data.shift.status;
        setStatus(shiftStatus);
        if (!isShiftViewable(shiftStatus)) {
          toast.error(shiftNotViewableMessage(shiftStatus));
          navigate('/', { replace: true });
          return;
        }
        if (shiftStatus === 'active') {
          navigate(`/shifts/${id}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id, navigate]);

  if (!id) return <Navigate to="/" replace />;

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      // The server starts the shift on the device bound to this session at sign-in.
      const res = await cashierStartShift(id, {
        denominations: denominations.filter((d) => d.quantity > 0),
      });
      if (!res.success) {
        if (isStartBlockerCode(res.code)) {
          // This device cannot start the shift (not approved, blocked, pinned elsewhere, busy, unknown):
          // the start page explains why and waits for approval when that is the reason.
          toast.error(describeDeviceError(res.code)!.message);
          navigate(`/shifts/${id}/start`, { replace: true });
          return;
        }
        toast.error(res.message || res.code || 'Failed to start shift');
        return;
      }
      toast.success('Shift started');
      navigate(`/shifts/${id}`, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CashierLayout title="Opening cash count" backTo="/">
        <div className="text-sm text-stone-600">Loading…</div>
      </CashierLayout>
    );
  }

  if (status && status !== 'scheduled') {
    return <Navigate to={`/shifts/${id}`} replace />;
  }

  return (
    <CashierLayout title="Opening cash count" subtitle="Count bills and coins in the drawer" backTo="/">
      <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
        <p className="text-sm text-stone-600">
          Enter the quantity for each denomination. The system will calculate your opening cash total.
        </p>
        <div className="mt-4">
          <DenominationGrid value={denominations} onChange={setDenominations} disabled={submitting} />
        </div>
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={submitting}
          className="mt-6 w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          {submitting ? 'Starting shift…' : SHIFT.startShift}
        </button>
      </div>
    </CashierLayout>
  );
}
